/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSession, getSession } from "../lib/auth-client";
import { sendSignupOtp, getSignupVerifyStatus, resendSignupVerifyLink } from "../lib/api";
import { api } from "../lib/api";
import { Icon } from "../components/Common.jsx";
import AuthSide from "../components/AuthSide.jsx";
import WhatsAppVerifyStep from "../components/WhatsAppVerifyStep.jsx";
import PhoneInput from "../components/PhoneInput.jsx";
import PeekMascot from "../components/PeekMascot.jsx";
import { detectDefaultCountry } from "../lib/timezone-country";

const maskPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return raw;
  const last4 = digits.slice(-4);
  const cc = digits.length > 10 ? digits.slice(0, digits.length - 10) : "";
  return `${cc ? "+" + cc + " " : ""}******${last4}`;
};

export default function CompleteSignupPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const [stage, setStage] = useState("form"); // 'form' | 'otp' | 'done'

  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [detectedCountry] = useState(() => detectDefaultCountry());
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [verificationToken, setVerificationToken] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [waLink, setWaLink] = useState("");
  const [botPhone, setBotPhone] = useState("");
  const [verifyMessage, setVerifyMessage] = useState("");
  const [verifyExpiresAt, setVerifyExpiresAt] = useState("");

  // If the user already has a companyId, they're already complete — redirect.
  useEffect(() => {
    if (!isPending && session?.user?.companyId) {
      navigate("/app", { replace: true });
    }
  }, [isPending, session, navigate]);

  if (isPending) {
    return (
      <div className="auth-page">
        <div className="auth-form-wrap" style={{ alignItems: "center", justifyContent: "center" }}>
          <p style={{ color: "var(--text-muted)" }}>Loading…</p>
        </div>
      </div>
    );
  }

  // No session — shouldn't happen because the route guard requires one, but
  // handle gracefully just in case.
  if (!session?.session) {
    navigate("/signin", { replace: true });
    return null;
  }

  const user = session.user;

  const onPhoneChange = ({ e164, isValid, national }) => {
    setPhone(e164);
    setPhoneValid(isValid);
    if (!national) setPhoneError("Phone is required");
    else if (!isValid) setPhoneError("Enter a valid phone number");
    else setPhoneError("");
  };

  // Stage 1: form submit → request OTP
  const submitForm = async (e) => {
    e.preventDefault();
    setError("");

    if (!companyName.trim()) {
      setError("Company name is required.");
      return;
    }
    if (!phone || !phoneValid || phoneError) {
      setError("Please enter a valid phone number.");
      return;
    }

    setNormalizedPhone(phone);

    setSubmitting(true);
    try {
      const data = await sendSignupOtp(phone);
      setVerificationToken(data.verificationToken);
      setWaLink(data.waLink);
      setBotPhone(data.botPhone);
      setVerifyMessage(data.verifyMessage);
      setVerifyExpiresAt(data.expiresAt);
      setStage("otp");
    } catch (err) {
      setError(err.message || "Could not start phone verification.");
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 2: phone verified → call POST /api/complete-signup
  const handleVerified = async () => {
    setSubmitting(true);
    try {
      await api.post("/api/complete-signup", {
        companyName: companyName.trim(),
        phone: normalizedPhone,
        verificationToken,
      });

      // Refresh the session so it picks up the new companyId, then navigate.
      await getSession();
      setStage("done");
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err.message || "Could not complete your signup.");
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 2 UI: OTP verification
  if (stage === "otp") {
    return (
      <div className="auth-page">
        <AuthSide
          title="Almost there — verify your phone."
          subtitle="We need your phone number so the WhatsApp report bot can reach you reliably."
          stats={[
            { v: "10 min", l: "Link valid for" },
            { v: "WhatsApp", l: "Click-to-verify" },
            { v: "1-time", l: "Per signup" },
          ]}
          chatVariant="B"
        />
        <div className="auth-form-wrap">
          <div className="auth-form">
            <h1>Verify your phone</h1>

            {error && (
              <div
                role="alert"
                style={{
                  marginBottom: 16,
                  padding: "10px 14px",
                  borderRadius: 10,
                  fontSize: 13,
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.4)",
                  color: "#F87171",
                }}
              >
                {error}
              </div>
            )}

            <WhatsAppVerifyStep
              token={verificationToken}
              waLink={waLink}
              botPhone={botPhone}
              verifyMessage={verifyMessage}
              phone={maskPhone(normalizedPhone)}
              expiresAt={verifyExpiresAt}
              onVerified={handleVerified}
              getStatus={getSignupVerifyStatus}
              doResend={resendSignupVerifyLink}
            />

            <div className="auth-foot">
              Wrong number?{" "}
              <a href="#" onClick={(ev) => { ev.preventDefault(); setStage("form"); }}>
                Go back
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Stage 1 UI: collect company name + phone
  return (
    <div className="auth-page">
      <AuthSide
        title="Set up your workspace."
        subtitle="Just need a couple more details to get you started."
        stats={[
          { v: "14 days", l: "Free trial" },
          { v: "5 min", l: "Setup time" },
          { v: "No card", l: "Required" },
        ]}
        chatVariant="B"
      />
      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submitForm}>
          <PeekMascot covered={false} />

          <h1>Complete your signup</h1>
          <p style={{ marginBottom: 4 }}>
            Welcome, <strong style={{ color: "var(--text)" }}>{user.name || user.email}</strong>!
          </p>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 0, marginBottom: 20 }}>
            Signed in with {user.email} via Google
          </p>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 13,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.4)",
                color: "#F87171",
              }}
            >
              {error}
            </div>
          )}

          <div className="field">
            <label>Company name</label>
            <div className="input-group">
              <Icon name="building-2" size={16} />
              <input
                type="text"
                className="input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Pvt Ltd"
                required
              />
            </div>
          </div>

          <div className="field">
            <label>Phone</label>
            <PhoneInput
              onChange={onPhoneChange}
              defaultCountry={detectedCountry}
              error={phoneError}
              disabled={submitting}
            />
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={submitting || !!phoneError || !phoneValid || !companyName.trim()}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {submitting ? (
              "Sending verification code…"
            ) : (
              <>
                Continue <Icon name="arrow-right" size={16} />
              </>
            )}
          </button>

          <div className="auth-foot" style={{ marginTop: 16 }}>
            <a
              href="#"
              onClick={(ev) => {
                ev.preventDefault();
                // Sign out and go back to sign-in
                import("../lib/auth-client").then(({ signOut }) => {
                  signOut();
                  navigate("/signin", { replace: true });
                });
              }}
              style={{ color: "var(--text-muted)", fontSize: 13 }}
            >
              Not you? Sign out
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
